import React from "react"
import { useRouter } from "next/router"
import { getApiUrlLocale } from "@/helpers"
import Fade from "react-reveal/Fade"
import Sticky from "react-stickynode"
import styled from "styled-components"
import { themeGet } from "@styled-system/theme-get"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import ResetCSS from "@/common/assets/css/style"
import NavbarNew from "@/containers/common/NavbarNew"
import Footer from "@/containers/common/FooterNew"
import SectionLine from "@/common/components/SectionLine"
import MetaData from "@/common/components/MetaData"
import Link from "next/link"
import Button from "@/common/components/Button"
import Container from "@/common/components/UI/Container"
import { AppWrapper, ContentWrapper } from "@/containers/common/style"

export const getStaticProps = async ({ locale }) => {
  const res = await fetch(
    `${
      process.env.NEXT_PUBLIC_CMS_HOST
    }/api/blogs?populate=*&locale=${getApiUrlLocale(
      locale
    )}&sort[0]=id%3Adesc&pagination[pageSize]=4`,
    {
      headers: {
        "Cache-Control": "s-maxage=1, stale-while-revalidate"
      }
    }
  )
  const data = await res.json()

  return {
    props: {
      articles: data,
      ...(await serverSideTranslations(locale, ["common", "contactUs"]))
    },
    revalidate: 300
  }
}

const MediumArticleCard = ({ item, locale, t }) => {
  const imgUrl =
    item?.attributes?.cover_image?.data?.attributes?.formats?.medium?.url ||
    item?.attributes?.cover_image?.data?.attributes?.url // GIF

  return (
    <CardWrapper>
      <Link
        href={`${locale === "en" ? "" : `/${locale}`}/blog/${
          item.attributes.slug
        }`}
        key={item.attributes.slug}
        legacyBehavior
      >
        <div>
          <ImageWrapper>
            <img alt="" src={imgUrl} />
          </ImageWrapper>
          <TextWrapper>
            <CardTitle>{item.attributes.title}</CardTitle>
            <CardContent>{item.attributes.description}</CardContent>
            <CardLink>{t("button.cta-read-more")}</CardLink>
          </TextWrapper>
        </div>
      </Link>
    </CardWrapper>
  )
}

const Custom404 = ({ articles }) => {
  const router = useRouter()
  const { t } = useTranslation("common")
  const { locale } = useRouter()

  return (
    <React.Fragment>
      <div>
        <MetaData
          title={t("error.article.desc")}
          description={t("error.article.desc")}
          url={`https://www.kryptogo.com${
            locale === "en" ? "" : `/${locale}`
          }/blog/404-blog`}
          image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
          languageAlternates={[
            [
              {
                hrefLang: "en-US",
                href: "https://www.kryptogo.com/blog/404-blog"
              },
              {
                hrefLang: "zh-TW",
                href: "https://www.kryptogo.com/tw/blog/404-blog"
              },
              {
                hrefLang: "zh-CN",
                href: "https://www.kryptogo.com/cn/blog/404-blog"
              }
            ]
          ]}
        />
        <ResetCSS />
        {/* end of global and reset style */}
        {/* start app classic landing */}
        <AppWrapper>
          <Sticky top={0} innerZ={1000} activeClass="sticky-active">
            <NavbarNew />
          </Sticky>
          <Container>
            <ContentWrapper>
              <Section404 className="E404Section">
                <div>
                  <h1 dataspotlight="404">404</h1>
                  <div>
                    <h2>{t("error.article.desc")}</h2>
                    <Link href={`/${locale}/blog`} legacyBehavior>
                      <Button title={t("error.article.btn")} />
                    </Link>
                  </div>
                </div>
              </Section404>
              <SectionLine style={{ marginTop: "-150px" }} />
              <Fade bottom>
                <h2 style={{ textAlign: "center" }}>
                  {t("error.or")}
                  <br />
                  <br />
                  <br />
                </h2>
              </Fade>
              <SectionLine />
              <div
                style={{
                  display: "inline-flex",
                  flexWrap: "wrap"
                }}
              >
                {articles?.data?.map(item => (
                  <MediumArticleCard
                    item={item}
                    key={item.id}
                    locale={locale}
                    t={t}
                  />
                ))}
              </div>
              <SectionLine />
            </ContentWrapper>
          </Container>
          <Footer />
        </AppWrapper>
      </div>
    </React.Fragment>
  )
}

export default Custom404

const Section404 = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;

  h1 {
    font-size: 20rem;
    position: relative;
    color: ${themeGet("colors.secondaryLight")};
    text-align: center;
    margin-top: 0px;

    &::before {
      content: "404";
      font-size: 20rem;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 0;
      clip-path: ellipse(200px 200px at 0% 50%);
      animation: spotlight 4s infinite ease-in-out;
      color: transparent;
      background-color: #fbab7e;
      background-image: linear-gradient(
        62deg,
        #ffea05 10%,
        #f7ce68 40%,
        ${themeGet("colors.primary")} 100%
      );
      -webkit-background-clip: text;
      text-shadow: 1px 1px 30px rgba(238, 168, 17, 0.773);
    }

    &::after {
      content: "404";
      font-size: 20rem;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 0;
      clip-path: ellipse(200px 200px at 0% 50%);
      animation: spotlight 4s infinite ease-in-out;
      color: transparent;
      background-color: #fbab7e;
      background-image: linear-gradient(
        62deg,
        #ffea05 10%,
        #f7ce68 40%,
        ${themeGet("colors.primary")} 100%
      );
      -webkit-background-clip: text;
    }
  }

  div:nth-child(2) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;

    h2 {
      display: inline-block;
      margin-right: 50px;
    }

    h2 {
      margin: 0px !important;
    }

    button {
      margin: 0px;
    }
  }

  @media (max-width: 800px) {
    h1 {
      font-size: 10rem;

      &::before {
        content: "404";
        font-size: 10rem;
        clip-path: ellipse(200px 200px at 0% 50%);
        text-shadow: 1px 1px 20px rgba(238, 168, 17, 0.773);
      }

      &::after {
        content: "404";
        font-size: 10rem;
        clip-path: ellipse(200px 200px at 0% 50%);
      }
    }

    div:nth-child(2) {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 24px;
      flex-direction: column;

      h2 {
        display: inline-block;
        margin-right: 50px;
        margin-top: 50px;
      }

      button {
        margin: 0px;
        margin-top: 10px;
      }
    }
  }

  @keyframes spotlight {
    0% {
      clip-path: ellipse(200px 200px at -40% 50%);
    }

    50% {
      clip-path: ellipse(300px 300px at 160% 50%);
    }

    100% {
      clip-path: ellipse(200px 200px at -40% 50%);
    }
  }
`

const TitleWrapper = styled.div`
  padding-top: 160px;
  @media (max-width: 768px) {
    padding-top: 100px;
  }
`
const CardWrapper = styled.div`
  width: 50%;
  display: inline-block;
  padding: 2rem;
  @media screen and (max-width: 1024px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  @media screen and (max-width: 768px) {
    width: 100%;
    border-bottom: 1px solid ${themeGet("colors.primaryLight")};
  }
  a {
    display: block;
  }

  &:hover {
    h4 {
      color: ${themeGet("colors.primary")};
    }
    p {
      color: ${themeGet("colors.title")};
    }
    h6 {
      // transform: translateX(-10%);
      &:after {
        opacity: 1;
        width: 10%;
      }
      &:before {
        width: 0;
      }
    }
    img {
      transform: scale(1.2);
      opacity: 0.8;
    }
  }
`

const ImageWrapper = styled.div`
  background-color: #ccc;
  width: 100%;
  height: 300px;
  overflow: hidden;
  background-color: ${themeGet("colors.secondary")};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: 0.5s ease;
  }
  @media (max-width: 1024px) {
    height: 200px;
  }
`

const TextWrapper = styled.div`
  padding: 2rem;
  padding-right: 0;
  @media (max-width: 1024px) {
    padding: 2rem 0;
  }
`

const CardTitle = styled.h4`
  font-weight: 700;
  height: 40px;
  transition: 0.3s ease;
`

const CardContent = styled.p`
  transition: 0.3s ease;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CardLink = styled.h6`
  width: 100%;
  color: ${themeGet("colors.primary")};
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  transition: 0.3s ease;
  @media (max-width: 600px) {
    justify-content: flex-start;
  }

  &:before,
  &:after {
    content: "";
    display: inline-block;
    width: 10%;
    height: 1px;
    background-color: ${themeGet("colors.primary")};
    margin: 0 10px;
    transition: 0.3s ease;
  }
  &:after {
    opacity: 0;
    width: 0;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 100px 0;
`
